import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
} from "reactstrap"
import logo from "../../assets/images/Shukre_Ilahi_2.png"
import Loader from "components/Common/loader"
import { MapDataToApplicationState } from "utilities/mapDataToApplicationState"
import { SharedApplicationFetch } from "redux/slice/applications/applicationSlice"

import PersonalInfoForm from "pages/Applications/formSections/personal"
import AcademicsInfoForm from "pages/Applications/formSections/academics"
import EducationExpensesInfoForm from "pages/Applications/formSections/education-expenses"
import DocumentsInfoForm from "pages/Applications/formSections/documents"
import ReferenceInfoForm from "pages/Applications/formSections/reference"
import AuthorizationInfoForm from "pages/Applications/formSections/authorization"

const noop = () => {}

export default function SharedApplication() {
  document.title = "Shared Application | Shukre Ilahi"
  const dispatch = useDispatch()
  const { id } = useParams()

  const storageKey = `shuk_shared_code_${id}`

  const [phase, setPhase] = useState("gate") 
  const [code, setCode] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [formData, setFormData] = useState(null)

  const [bootstrapping, setBootstrapping] = useState(true)

  const readStoredCode = () => {
    try {
      return sessionStorage.getItem(storageKey)
    } catch {
      return null
    }
  }

  const verify = async rawCode => {
    const trimmed = (rawCode || "").trim()
    if (!/^\d{8}$/.test(trimmed)) {
      setError("Please enter the 8-digit access code.")
      return false
    }
    setError("")
    setLoading(true)
    const res = await dispatch(SharedApplicationFetch({ code: trimmed }))
    setLoading(false)

    const payload = res?.payload
    const ok =
      payload &&
      !payload.error &&
      (payload.status === 200 || payload.status === 201)

    if (!ok) {
      setError("Invalid or expired code. Please check and try again.")
      try {
        sessionStorage.removeItem(storageKey)
      } catch {}
      return false
    }

    const body = payload.data || {}
    const studentInfo =
      body?.studentInfo ||
      body?.data?.studentInfo ||
      body?.data ||
      body?.items ||
      body

    try {
      setFormData(MapDataToApplicationState(studentInfo))
      setPhase("view")
      try {
        sessionStorage.setItem(storageKey, trimmed)
      } catch {}
      return true
    } catch (err) {
      console.log("error mapping shared application data", err)
      setError("Something went wrong loading this application.")
      return false
    }
  }

  useEffect(() => {
    const stored = readStoredCode()
    if (stored && /^\d{8}$/.test(stored)) {
      setCode(stored)
      verify(stored).finally(() => setBootstrapping(false))
    } else {
      setBootstrapping(false)
    }
  }, [])

  const handleSubmit = e => {
    e.preventDefault()
    verify(code)
  }

  if (bootstrapping) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Loader />
      </div>
    )
  }

  if (phase === "gate") {
    return (
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardBody className="pt-0">
                  <h3 className="text-center mt-5 mb-4">
                    <span className="d-block auth-logo">
                      <img src={logo} alt="Shukre Ilahi" height="200" />
                    </span>
                  </h3>
                  <div className="p-3">
                    <h4 className="text-muted font-size-18 mb-1 text-center">
                      Shared Application
                    </h4>
                    <p className="text-muted text-center">
                      Enter the 8-digit access code from your email to view this
                      application.
                    </p>

                    {error ? <Alert color="danger">{error}</Alert> : null}

                    <Form className="form-horizontal mt-4" onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <Label htmlFor="shared-code">Access Code</Label>
                        <Input
                          id="shared-code"
                          name="code"
                          type="text"
                          inputMode="numeric"
                          maxLength={8}
                          placeholder="Enter 8-digit code"
                          value={code}
                          onChange={e =>
                            setCode(e.target.value.replace(/\D/g, ""))
                          }
                        />
                      </div>

                      <Row className="mb-3 mt-4">
                        <Col xs={12} className="text-end">
                          <button
                            className="btn btn-success w-md waves-effect waves-light"
                            type="submit"
                            disabled={loading || code.length !== 8}
                          >
                            {loading ? (
                              <div
                                className="spinner-border text-light"
                                role="status"
                                style={{ width: "1.1rem", height: "1.1rem" }}
                              />
                            ) : (
                              "View Application"
                            )}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                © {new Date().getFullYear()} Shukre Ilahi - Developed by Happy
                Digital.
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <div
      className="p-3 p-md-4"
      style={{ minHeight: "100vh", background: "#f5f6f8" }}
    >
      <div className="d-flex align-items-center mb-4">
        <img src={logo} alt="Shukre Ilahi" height="56" className="me-3" />
        <h3 className="mb-0 text-secondary-si">Application Details</h3>
      </div>

      <div className="card">
        <div className="card-body">
          {!formData ? (
            <Loader />
          ) : (
              <div className="row">
                <PersonalInfoForm
                  handleChangeInfo={noop}
                  personalInfo={formData.personal}
                  forView={true}
                />
                <AcademicsInfoForm
                  handleChangeInfo={noop}
                  academicsInfo={formData.academics}
                  forView={true}
                />
                <EducationExpensesInfoForm
                  handleChangeInfo={noop}
                  educationExpenseInfo={formData.educationExpenses}
                  academicsInfo={formData.academics}
                  forView={true}
                />
                <DocumentsInfoForm
                  handleChangeInfo={noop}
                  handleFileChange={noop}
                  handleFileRemove={noop}
                  documentsInfo={formData.documents}
                  forView={true}
                  componentFor="View"
                  role=""
                />
                <ReferenceInfoForm
                  handleChangeInfo={noop}
                  community={formData.refernce}
                  forView={true}
                />
                <AuthorizationInfoForm
                  handleChangeInfo={noop}
                  handleFileChange={noop}
                  handleFileRemove={noop}
                  authorizationInfo={formData.authorization}
                  forView={true}
                  hideIpAddress={true}
                />
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
